<!-- Represent a Ticker that runs on a Decoder.

Used as a list item.

-->

<template>
  <div>
    <v-list
      dense
      two-line
    >
      <v-list-tile
        class="
          ticker-values ticker-tile
          mt-1
          list-tile-align-start
          no-hover-clr no-click-pointer
        "
        :ripple="false"
      >
        <!-- Note: Don't add 'grow-0' here as it looks wrong on iOS -->
        <div
          class="ticker-item-group mb-1"
          style="height: 100%"
        >
          <v-layout>
            <transition
              name="slide-x-transition"
              mode="out-in"
            >
              <v-layout
                v-if="showSelectionCheckbox"
                class="mr-2"
                align-baseline
              >
                <v-checkbox
                  v-model="entry.selected"
                  color="msr"
                  hide-details
                />
              </v-layout>
            </transition>
            <div
              v-if="showPreview"
              class="clickable"
              :class="{
                'auto-height': tickerPreviewRunning,
                'mr-2': tickerPreviewRunning,
                tickerpreview: !tickerPreviewRunning,
              }"
            >
              <v-layout justify-center>
                <mini-preview-ticker
                  :message="entry.ticker.message"
                  @started="tickerPreviewRunning = true"
                  @stopped="tickerPreviewRunning = false"
                />
              </v-layout>
            </div>
          </v-layout>
        </div>
        <div class="ticker-item-group grow-3">
          <div class="ticker-item author grey--text text--darken-1">
            <span v-if="entry.ticker.creator && showAuthor">
              <v-layout
                row
                wrap
              >
                <span>{{ entry.ticker.creator.first_name || '' }}&nbsp;</span>
                <span>{{ entry.ticker.creator.last_name || '' }}</span>
              </v-layout>
            </span>
            <span v-else>{{ $t('ticker') }}</span>
          </div>
          <div
            v-if="showMessage"
            class="ticker-item grow-3 mr-2 ticker-message clickable"
            @click="goToTicker(entry.ticker.id)"
          >
            <q
              class="ticker-quote"
              :class="{'msr--text': showSelectionCheckbox && entry.selected}"
            >{{ entry.ticker.message.trim() }}</q>
          </div>
          <div
            v-if="showTiming && entry.has_timing"
            class="ticker-item grow-1 timing"
            :class="getTimingCSS(entry, showActions)"
            @click="showActions ? (showEntryTimingDialog = true) : null"
          >
            <small>
              {{ entry.startdate | dayjs('DD.MM.YYYY') }}
              <span
                v-if="entry.stopdate !== null"
              >- {{ entry.stopdate | dayjs('DD.MM.YYYY') }}</span>
            </small>
          </div>
        </div>
        <!-- Actions Menu -->
        <div class="ticker-item-group grow-0 ml-1 last">
          <v-layout
            column
            align-center
          >
            <v-menu
              v-if="showActions"
              offset-y
              left
            >
              <v-icon
                slot="activator"
                :medium="$vuetify.breakpoint.xsOnly"
              >
                more_vert
              </v-icon>
              <v-list>
                <v-list-tile @click="showEntryTimingDialog = true">
                  <v-list-tile-title class="menu-item">
                    <v-icon
                      class="mr-2"
                      :class="{
                        'msr--text': entry.has_timing,
                        'grey--text': !entry.has_timing,
                      }"
                    >
                      timer
                    </v-icon>
                    {{ $t('timing') }}
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  :disabled="!removalAllowed"
                  @click="showRemoveTickerFromDecoderDialog = true"
                >
                  <v-list-tile-title class="menu-item">
                    <v-icon class="grey--text mr-2">
                      block
                    </v-icon>
                    {{ $t('deactivate') }}
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
            <div
              v-if="showStatus"
              class="spot-status"
            >
              <div v-if="entry.has_timing">
                <v-icon
                  class="msr--text mt-1"
                  style="font-size: 1.25em"
                  @click="showEntryTimingDialog = true"
                >
                  timer
                </v-icon>
              </div>
              <div
                v-if="entry.has_timing"
                class="mt-1"
              />
            </div>
          </v-layout>
        </div>
      </v-list-tile>
    </v-list>
    <!-- Entry Timing Dialog -->
    <entry-timing-dialog
      :show.sync="showEntryTimingDialog"
      :entries="[entry]"
      :show-timeframe-controls="false"
      @submitted="$emit('changed')"
    />
    <!-- Removal Dialog -->
    <v-dialog
      v-model="showRemoveTickerFromDecoderDialog"
      absolute
    >
      <v-card>
        <v-card-title>
          <div class="headline">
            {{ $t('askDeleteEntry') }}
          </div>
          <v-btn
            style="position: absolute; top: 0; right: 0"
            flat
            icon
            :title="$t('close')"
            @click="showRemoveTickerFromDecoderDialog = false"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            round
            @click="removeTickerFromDecoder(entry.ticker, entry.decoder)"
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-divider v-if="showDivider" />
  </div>
</template>

<script>
import commonTicker from '@/mixins/commonTicker.js';

import BtnOk from '@/components/ButtonOk';
import EntryTimingDialog from '@/components/EntryTimingDialog';
import MiniPreviewTicker from '@/components/MiniPreviewTicker';

export default {
  components: {
    BtnOk,
    EntryTimingDialog,
    MiniPreviewTicker,
  },
  mixins: [commonTicker],
  props: {
    /* A TickerEntry object. */
    entry: {
      type: Object,
      required: true,
    },
    /* Whether to enable the 'remove this entry' menu action. */
    removalAllowed: {
      type: Boolean,
      default: true,
    },
    /* All props below control column visibility. */
    showSelectionCheckbox: {
      type: Boolean,
      default: false,
    },
    showPreview: {
      type: Boolean,
      default: true,
    },
    showMessage: {
      type: Boolean,
      default: true,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    showAuthor: {
      type: Boolean,
      default: false,
    },
    showTiming: {
      type: Boolean,
      default: true,
    },
    showStatus: {
      type: Boolean,
      default: true,
    },
    showDivider: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      tickerPreviewRunning: false,
      showEntryTimingDialog: false,
    };
  },
  methods: {
    goToTicker(tickerId) {
      let path = '/ticker/' + tickerId;
      this.$router.push(path);
    },
  },
};
</script>

<style>
/* Ensure full width for v-list-tile. */
.ticker-tile .v-list__tile {
  width: 100%;
}
.v-list--two-line.v-list--dense .ticker-tile .v-list__tile {
  height: auto;
}

/* Use custom quote characters. */
q.ticker-quote {
  quotes: '\00ab''\00bb';
}
</style>

<style scoped>
.back-btn {
  margin: 0;
  padding: 0;
}

.menu-item {
  display: flex;
  align-items: center;
}

.ticker-values {
  display: flex;
}

.ticker-item {
  margin: 0.1em 0 0.1em 0;
  word-break: break-word;
}
.ticker-item.last {
  margin-right: 0;
}

.ticker-item-group {
  display: flex;
  flex-flow: row nowrap;
}
.ticker-item-group.last {
  justify-content: flex-end;
  align-items: flex-end;
}
@media (max-width: 959px) {
  .ticker-item-group {
    flex-flow: column nowrap;
  }
  .ticker-item-group.last {
    justify-content: flex-start;
    align-items: flex-end;
  }
}

.author {
  min-width: 80px;
  max-width: 96px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tickerpreview {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  height: 54px;
  width: 96px;
  margin-right: 1em;
}

i.tickerpreview-icon {
  font-size: 4.5em !important;
}

.ticker-quote {
  font-size: 1.15em;
  font-weight: lighter;
}

.spot-status {
  height: 55px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
@media (max-width: 599px) {
  .spot-status {
    height: 75px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/TickerEntry.vue